import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Grid,
  Select,
  Heading,
  IconButton,
  Box,
  VStack,
  Stack,
} from "@chakra-ui/react";
import { X } from "lucide-react";
import { procedureService } from "@/lib/services/procedureService";
import { Procedures } from "@/types/procedures";

interface AddProcedureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (procedure: Procedures) => void;
  availableProcedures?: Procedures[];
  editingProcedure?: Procedures | null;
}

const AddProcedureModal: React.FC<AddProcedureModalProps> = ({
  isOpen,
  onClose,
  onSave,
  availableProcedures = [],
  editingProcedure = null,
}) => {
  const [selectedProcedure, setSelectedProcedure] = useState<Procedures | null>(
    null,
  );
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");

  // Dummy ref to prevent auto focus
  const initialRef = useRef<HTMLDivElement>(null);

  // Pre-fill form when editing
  useEffect(() => {
    if (isOpen && editingProcedure) {
      const matchingProcedure = availableProcedures.find(
        (p) => p.name === editingProcedure.name,
      );
      setSelectedProcedure(matchingProcedure || null);
      setDescription(editingProcedure.description);
      setPrice(editingProcedure.price.toString());
    } else if (!isOpen) {
      // Reset modal when closed
      setSelectedProcedure(null);
      setDescription("");
      setPrice("");
    }
  }, [isOpen, editingProcedure, availableProcedures]);

  // Update description & price when procedure changes (only when not editing)
  useEffect(() => {
    if (!editingProcedure && selectedProcedure) {
      setDescription(selectedProcedure.name);
      setPrice(selectedProcedure.price.toString());
    } else if (!editingProcedure && !selectedProcedure) {
      setDescription("");
      setPrice("");
    }
  }, [selectedProcedure, editingProcedure]);

  const handleSave = () => {
    if (!selectedProcedure || !price) return;
    onSave({
      name: selectedProcedure.name,
      description,
      price: Number(price),
      procedure_id: selectedProcedure.procedure_id || selectedProcedure.id,
    });
    onClose();
  };

  const handleClose = () => {
    onClose();
    // Optional: Reset form on close
    setSelectedProcedure(null);
    setDescription("");
    setPrice("");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={{ base: "full", sm: "md", md: "lg", lg: "xl", xl: "xl" }}
      scrollBehavior="inside"
      initialFocusRef={initialRef}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius={{ base: 0, md: "none" }}
        mx={{ base: 0, md: 4 }}
        maxH={{ base: "100vh", md: "90vh" }}
      >
        <ModalHeader
          fontSize={{ base: "lg", md: "xl" }}
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          position="relative"
        >
          {editingProcedure ? "Edit Procedure" : "Add Procedure"}
          {/* Close Button */}
          <IconButton
            aria-label="Close Modal"
            icon={<X size={18} />}
            onClick={handleClose}
            position="absolute"
            right={{ base: 3, md: 5 }}
            top={{ base: 3, md: 5 }}
            size={{ base: "sm", md: "md" }}
            variant="link"
            color="#0A1B39"
            _hover={{ color: "#DC3611" }}
            _focus={{ boxShadow: "none" }}
          />
        </ModalHeader>

        <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

        <ModalBody
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          overflowY="auto"
        >
          {/* Dummy div for initial focus */}
          <div ref={initialRef} tabIndex={-1} />

          <Heading
            size={{ base: "xs", sm: "sm", md: "sm" }}
            mb={{ base: 3, md: 4 }}
            fontWeight="semibold"
            color="#0A1B39"
          >
            Procedure Information
          </Heading>

          <VStack spacing={{ base: 3, md: 4 }} align="stretch">
            {/* Mobile: Stacked layout */}
            <Box display={{ base: "block", sm: "none" }}>
              <VStack spacing={3} align="stretch">
                {/* Procedure Name Dropdown */}
                <FormControl isRequired>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Procedure Name
                  </FormLabel>
                  <Select
                    placeholder="Select procedure"
                    size="sm"
                    fontSize="xs"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    h="36px"
                    minH="36px"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    value={selectedProcedure?.id || ""}
                    onChange={(e) => {
                      const found = availableProcedures.find(
                        (p) => p.id === Number(e.target.value),
                      );
                      setSelectedProcedure(
                        found
                          ? { ...found, procedure_id: Number(e.target.value) }
                          : null,
                      );
                    }}
                  >
                    {availableProcedures.map((procedure) => (
                      <option key={procedure.id} value={procedure.id}>
                        {procedure.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Price */}
                <FormControl isRequired>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Price
                  </FormLabel>
                  <Input
                    placeholder="₱0.00"
                    type="number"
                    min={0}
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize="xs"
                    h="36px"
                    minH="36px"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </FormControl>

                {/* Description */}
                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Description
                  </FormLabel>
                  <Textarea
                    placeholder="Type Here"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize="xs"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    minH="80px"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </Box>

            {/* Desktop/Tablet: Grid layout */}
            <Box display={{ base: "none", sm: "block" }}>
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Grid
                  templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                  gap={{ base: 3, md: 4 }}
                >
                  {/* Procedure Name Dropdown */}
                  <FormControl isRequired>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Procedure Name
                    </FormLabel>
                    <Select
                      placeholder="Select procedure"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={selectedProcedure?.id || ""}
                      onChange={(e) => {
                        const found = availableProcedures.find(
                          (p) => p.id === Number(e.target.value),
                        );
                        setSelectedProcedure(
                          found
                            ? { ...found, procedure_id: Number(e.target.value) }
                            : null,
                        );
                      }}
                    >
                      {availableProcedures.map((procedure) => (
                        <option key={procedure.id} value={procedure.id}>
                          {procedure.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Price */}
                  <FormControl isRequired>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Price
                    </FormLabel>
                    <Input
                      placeholder="₱0.00"
                      type="number"
                      min={0}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      fontSize={{ base: "xs", md: "sm" }}
                      h={{ base: "36px", md: "40px" }}
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </FormControl>
                </Grid>

                {/* Description */}
                <FormControl>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Description
                  </FormLabel>
                  <Textarea
                    placeholder="Type Here"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize={{ base: "xs", md: "sm" }}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    minH="100px"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

        <ModalFooter
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          gap={{ base: 2, md: 3 }}
          flexWrap="wrap"
        >
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={{ base: 2, md: 3 }}
            w="full"
            justify="flex-end"
          >
            <Button
              variant="outline"
              onClick={handleClose}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "sm", md: "md" }}
            >
              Cancel
            </Button>
            <Button
              bg="#DC3611"
              color="white"
              _hover={{ bg: "#bf3c1fff" }}
              onClick={handleSave}
              isDisabled={!selectedProcedure || !price}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "sm", md: "md" }}
            >
              {editingProcedure ? "Save Changes" : "Add Procedure"}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddProcedureModal;
